import { Task, TaskCategory, TaskPriority, TaskStatus, ActiveGuest } from '../interfaces/property-visit.interface';

/**
 * Property Visit Mapper
 *
 * Converts raw API response data to the shape the UI expects.
 * When the real API is ready, update the field names here to match
 * the actual response keys without touching any UI components.
 */

export interface PropertyVisitListItem {
  id: string;
  visit_date: string;
  property_id: string;
  property_name: string;
  property_city: string;
  visit_type: string;
  visit_type_label: string;
  visited_by_id: string;
  visited_by_name: string;
  visited_by_role: string;
  visited_by_role_label: string;
  visit_note: string;
  visit_status: string;
  visit_status_label: string;
  internal_note: string;
  has_internal_note: boolean;
  tasks_count: number;
  tasks_pending_count: number;
  tasks_done_count: number;
  updated_by_name: string;
  updated_at: string;
}

export interface PropertyVisitKpis {
  to_be_reviewed: number;
  needs_clarification: number;
  completed: number;
  not_qualified: number;
}

export interface PropertyVisitListResponse {
  records: PropertyVisitListItem[];
  total: number;
  kpis: PropertyVisitKpis;
}

export const mapPropertyVisitListItem = (raw: any): PropertyVisitListItem => ({
  id: raw.id?.toString() ?? '',
  visit_date: raw.visit_date ?? '-',
  property_id: raw.property?.id?.toString() ?? raw.property_id ?? '',
  property_name: raw.property?.name ?? raw.property_name ?? '-',
  property_city: raw.property?.city_name ?? raw.property?.city ?? raw.property_city ?? '-',
  visit_type: raw.visit_type ?? '',
  visit_type_label: raw.visit_type_name ?? raw.visit_type_label ?? '-',
  visited_by_id: raw.visited_by?.id?.toString() ?? raw.visited_by_id ?? '',
  visited_by_name: raw.visited_by?.name ?? raw.visited_by_name ?? '-',
  visited_by_role: raw.visited_by?.user_role ?? raw.visited_by_role ?? '',
  visited_by_role_label: raw.visited_by?.user_role_name ?? raw.visited_by_role_label ?? '-',
  visit_note: raw.visit_note ?? '-',
  visit_status: raw.status ?? raw.visit_status ?? '',
  visit_status_label: raw.status_name ?? raw.visit_status_label ?? '-',
  internal_note: raw.internal_note ?? '',
  has_internal_note: !!(raw.internal_note),
  tasks_count: raw.tasks?.length ?? raw.tasks_count ?? 0,
  tasks_pending_count: raw.tasks_pending_count ?? 0,
  tasks_done_count: raw.tasks_done_count ?? 0,
  updated_by_name: raw.last_updated_by ?? raw.updated_by_name ?? '-',
  updated_at: raw.updated_at ?? '-',
});

export const mapPropertyVisitList = (response: any): PropertyVisitListResponse => ({
  records: (response?.records ?? []).map(mapPropertyVisitListItem),
  total: response?.total_records ?? response?.total ?? 0,
  kpis: {
    to_be_reviewed: response?.to_be_reviewed_count ?? 0,
    needs_clarification: response?.needs_clarification_count ?? 0,
    completed: response?.completed_count ?? 0,
    not_qualified: response?.not_qualified_count ?? 0,
  },
});

export interface LookupOption {
  id: string;
  name: string;
}

export const mapLookupOption = (raw: any): LookupOption => ({
  id: raw.id?.toString() ?? raw.value?.toString() ?? '',
  name: raw.name ?? raw.label ?? '-',
});

export const mapLookupList = (response: any): LookupOption[] =>
  (response?.records ?? response ?? []).map(mapLookupOption);


const toIsoDate = (date: string | null | undefined): string => {
  if (!date) return '';
  const [m, d, y] = date.split('/');
  return m && d && y ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : date;
};

export const mapTask = (raw: any): Task => ({
  id: raw.id?.toString() ?? '',
  description: raw.description ?? raw.task_description ?? '',
  category: (raw.category ?? raw.task_category ?? 'other') as TaskCategory,
  category_name: raw.category_name ?? raw.category ?? '',
  priority: (raw.priority ?? raw.task_priority ?? 'low') as TaskPriority,
  priority_name: raw.priority_name ?? raw.priority ?? '',
  status: (raw.status ?? raw.task_status ?? 'pending') as TaskStatus,
  status_name: raw.status_name ?? raw.status ?? '',
  due_date: raw.due_date ?? '',
});

export const mapTaskList = (response: any): Task[] =>
  (response?.records ?? response ?? []).map(mapTask);

export const mapActiveGuest = (raw: any): ActiveGuest | null => {
  const g = raw?.guest ?? raw;
  if (!g || (!g.guest_name && !g.guestName)) return null;
  return {
    guestName: g.guest_name ?? g.guestName ?? '',
    platform: g.platform?.name ?? g.platform ?? '',
    checkIn: g.check_in_date ?? g.check_in ?? g.checkIn ?? '',
    checkOut: g.check_out_date ?? g.check_out ?? g.checkOut ?? '',
    nights: g.total_nights ?? g.nights ?? 0,
    reservationId: g.reservation_id?.toString() ?? g.reservationId?.toString() ?? '',
  };
};
